import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { toast } from "sonner"

import { AdminPageHeader } from "@/components/AdminPageHeader"
import { DateToolbar, useKitchenDate } from "@/components/DateToolbar"
import { Button } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { api } from "@/lib/client"
import { formatAddress, formatDateLabel, windowLabel } from "@/lib/format"
import { queryKeys } from "@/lib/queryKeys"
import { ADMIN_TABLE_CLASSNAME, ADMIN_TABLE_HEAD_TH, ADMIN_TABLE_ROW, statusBadgeClass } from "@/lib/table"
import { DELIVERY_STATUS_LABELS, nextDeliveryStatus, type DeliveryStatus } from "@/lib/types"

export function KitchenTodayPage() {
  const [date, setDate] = useKitchenDate()
  const queryClient = useQueryClient()
  const day = useQuery({
    queryFn: () => api.kitchenDay(date),
    queryKey: queryKeys.kitchenDay(date),
  })

  const patch = useMutation({
    mutationFn: ({ orderId, status }: { orderId: number; status: DeliveryStatus }) =>
      api.patchDeliveryStatus(orderId, date, status),
    onError: (err) => toast.error(err.message),
    onSuccess: async () => {
      await queryClient.invalidateQueries()
    },
  })

  const bulk = useMutation({
    mutationFn: (status: DeliveryStatus) => api.bulkDayStatus(date, status),
    onError: (err) => toast.error(err.message),
    onSuccess: async (result) => {
      toast.success(`Updated ${result.updated} deliveries`)
      await queryClient.invalidateQueries()
    },
  })

  const deliveries = day.data?.deliveries ?? []

  return (
    <div>
      <AdminPageHeader
        description={`${formatDateLabel(date)} · ${deliveries.length} deliveries`}
        title="Today"
      >
        <DateToolbar date={date} onChange={setDate} />
        <Button
          disabled={bulk.isPending}
          onClick={() => bulk.mutate("preparing")}
          size="sm"
          type="button"
        >
          Start prep
        </Button>
        <Button
          disabled={bulk.isPending}
          onClick={() => bulk.mutate("out_for_delivery")}
          size="sm"
          type="button"
          variant="outline"
        >
          Out for delivery
        </Button>
      </AdminPageHeader>

      {day.isError ? (
        <p className="text-sm text-destructive">{day.error.message}</p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <Table className={ADMIN_TABLE_CLASSNAME}>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className={ADMIN_TABLE_HEAD_TH}>Order</TableHead>
                <TableHead className={ADMIN_TABLE_HEAD_TH}>Customer</TableHead>
                <TableHead className={ADMIN_TABLE_HEAD_TH}>Window</TableHead>
                <TableHead className={ADMIN_TABLE_HEAD_TH}>Address</TableHead>
                <TableHead className={ADMIN_TABLE_HEAD_TH}>Items</TableHead>
                <TableHead className={ADMIN_TABLE_HEAD_TH}>Allergens</TableHead>
                <TableHead className={ADMIN_TABLE_HEAD_TH}>Status</TableHead>
                <TableHead className={ADMIN_TABLE_HEAD_TH} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {day.isPending ? (
                <TableRow>
                  <TableCell className="text-muted-foreground" colSpan={8}>
                    Loading…
                  </TableCell>
                </TableRow>
              ) : deliveries.length === 0 ? (
                <TableRow>
                  <TableCell className="text-muted-foreground" colSpan={8}>
                    No deliveries for this date.
                  </TableCell>
                </TableRow>
              ) : (
                deliveries.map((row) => {
                  const next = nextDeliveryStatus(row.state)
                  return (
                    <TableRow className={ADMIN_TABLE_ROW} key={`${row.orderId}-${row.deliveryId}`}>
                      <TableCell>
                        <Link className="font-medium hover:underline" to={`/orders/${row.orderId}`}>
                          {row.orderName}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link className="hover:underline" to={`/customers/${row.customer.id}`}>
                          {row.customer.name || row.customer.email || "Customer"}
                        </Link>
                      </TableCell>
                      <TableCell className="capitalize">{windowLabel(row.window)}</TableCell>
                      <TableCell className="max-w-56 truncate text-muted-foreground">
                        {formatAddress(row.address)}
                      </TableCell>
                      <TableCell>{row.lineCount}</TableCell>
                      <TableCell className="max-w-40 truncate text-muted-foreground">
                        {row.allergens.join(", ") || "—"}
                      </TableCell>
                      <TableCell>
                        <span className={statusBadgeClass(row.state)}>
                          {DELIVERY_STATUS_LABELS[row.state as DeliveryStatus] ?? row.state}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button asChild size="sm" variant="ghost">
                            <Link to={`/orders/${row.orderId}/work-order/${date}`}>Work order</Link>
                          </Button>
                          {next ? (
                            <Button
                              disabled={patch.isPending}
                              onClick={() => patch.mutate({ orderId: row.orderId, status: next })}
                              size="sm"
                              type="button"
                              variant="outline"
                            >
                              {DELIVERY_STATUS_LABELS[next]}
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
